import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  FolderOpen, Image, VideoCamera, Sparkle, SquaresFour, Newspaper,
  Trash, DownloadSimple, Eye, Plus, MagnifyingGlass, FunnelSimple, PencilLine
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import { ShareResultButton } from '../components/ShareResultButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'all', label: 'All', icon: FolderOpen },
  { id: 'blog', label: 'Blog', icon: PencilLine },
  { id: 'visual_ads', label: '3D Ads', icon: Sparkle },
  { id: 'product_shoots', label: 'Product Shoots', icon: Image },
  { id: 'flyers', label: 'Flyers', icon: Newspaper },
  { id: 'carousels', label: 'Carousels', icon: SquaresFour },
  { id: 'animation_episodes', label: 'Animation', icon: VideoCamera },
  { id: 'launch_pages', label: 'Landing Pages', icon: Newspaper },
  { id: 'uploads', label: 'Uploads', icon: Image },
];

/* ───── Blog post list (used only when activeTab === 'blog') ─────────── */
const BlogPostList = ({ search }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/blog-post/list`);
      setPosts(data.posts || []);
    } catch { setPosts([]); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const filtered = search
    ? posts.filter((p) => (p.title || '').toLowerCase().includes(search.toLowerCase()) || (p.target_keyword || '').toLowerCase().includes(search.toLowerCase()))
    : posts;

  const remove = async (postId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this blog post? This cannot be undone.')) return;
    try { await axios.delete(`${API_URL}/api/blog-post/${postId}`); fetchPosts(); } catch {}
  };

  if (loading) {
    return (
      <div className="grid gap-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="rounded-xl animate-pulse" style={{ background: '#141414', height: 110 }} />
        ))}
      </div>
    );
  }

  if (filtered.length === 0) {
    return (
      <div className="py-16 text-center" data-testid="assets-blog-empty">
        <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-5"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <PencilLine size={36} style={{ color: 'rgba(245,240,232,0.25)' }} />
        </div>
        <h3 className="text-lg font-medium" style={{ color: 'rgba(245,240,232,0.5)', fontFamily: "'Instrument Serif', serif" }}>
          No blog posts yet.
        </h3>
        <p className="text-sm mt-2 max-w-sm mx-auto leading-relaxed" style={{ color: 'rgba(245,240,232,0.32)' }}>
          Your first post gets your business found on Google.
        </p>
        <button onClick={() => navigate('/dashboard/blog-post')} data-testid="assets-blog-write-first-btn"
          className="mt-6 px-5 py-2.5 rounded-xl text-sm font-medium" style={{ background: '#c8f060', color: '#080808' }}>
          Write your first post →
        </button>
      </div>
    );
  }

  const scoreColor = (s) => s === 'Strong' ? '#7be0ad' : s === 'Good' ? '#fcd34d' : '#f87171';

  return (
    <div className="grid gap-3" data-testid="assets-blog-list">
      {filtered.map((p) => (
        <div key={p.id} onClick={() => {
            const route = p.kind === 'case_study' ? '/dashboard/case-study' :
                          p.kind === 'faq' ? '/dashboard/faq' : '/dashboard/blog-post';
            navigate(`${route}?id=${p.id}`);
          }}
          data-testid={`blog-asset-card-${p.id}`}
          className="rounded-xl p-4 sm:p-5 cursor-pointer transition-all hover:border-white/15"
          style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {p.kind && p.kind !== 'blog_post' && (
              <span style={{
                display: 'inline-flex', padding: '3px 9px', borderRadius: 4,
                background: 'rgba(200,241,53,0.08)', border: '1px solid rgba(200,241,53,0.32)',
                color: '#c8f060',
                fontFamily: 'ui-monospace, monospace', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
              }}>{p.kind === 'case_study' ? 'Case study' : 'FAQ'}</span>
            )}
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '3px 9px', borderRadius: 4,
              background: `${scoreColor(p.seo_score)}1a`,
              border: `1px solid ${scoreColor(p.seo_score)}55`,
              color: scoreColor(p.seo_score),
              fontFamily: 'ui-monospace, monospace', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
            }}><span style={{ width: 5, height: 5, borderRadius: '50%', background: scoreColor(p.seo_score) }} />SEO: {p.seo_score}</span>
            <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 10, color: 'rgba(245,240,232,0.35)', letterSpacing: '0.14em', textTransform: 'uppercase' }}>
              {p.seo_checks?.word_count || '—'} words · {p.estimated_read_minutes || 5} min read
            </span>
            <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 10, color: 'rgba(245,240,232,0.35)', letterSpacing: '0.14em', textTransform: 'uppercase' }}>
              {p.status === 'published' ? 'Published' : 'Draft'}
            </span>
          </div>
          <h3 className="text-base sm:text-lg font-semibold mb-1.5" style={{ color: '#f5f0e8', fontFamily: "'Syne', sans-serif", letterSpacing: '-0.012em' }}>
            {p.title}
          </h3>
          <p className="text-xs" style={{ color: 'rgba(245,240,232,0.55)', fontFamily: 'ui-monospace, monospace' }}>
            Target: <span style={{ color: '#c8f060' }}>{p.target_keyword}</span>
            <span style={{ color: 'rgba(245,240,232,0.25)' }}> · {new Date(p.created_at).toLocaleDateString()}</span>
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            <button onClick={(e) => {
              e.stopPropagation();
              const route = p.kind === 'case_study' ? '/dashboard/case-study' :
                            p.kind === 'faq' ? '/dashboard/faq' : '/dashboard/blog-post';
              navigate(`${route}?id=${p.id}`);
            }}
              className="px-3 py-1.5 rounded-md text-[10px] font-bold tracking-wider uppercase"
              style={{ background: 'transparent', color: '#f5f0e8', border: '1px solid rgba(255,255,255,0.1)' }}>
              Open
            </button>
            <button onClick={(e) => remove(p.id, e)} data-testid={`blog-asset-delete-${p.id}`}
              className="px-3 py-1.5 rounded-md text-[10px] font-bold tracking-wider uppercase"
              style={{ background: 'transparent', color: '#f87171', border: '1px solid rgba(248,113,113,0.32)' }}>
              <Trash size={11} weight="bold" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

const AssetCard = ({ asset, type, onDelete }) => {
  const getImageUrl = () => {
    if (type === 'uploads') return `${API_URL}${asset.url}`;
    if (type === 'avatars') return `${API_URL}/api/characters/file/${asset.char_id}`;
    if (type === 'flyers') return asset.image_url ? `${API_URL}${asset.image_url}` : null;
    if (type === 'carousels') return `${API_URL}/api/carousel/image/${asset.carousel_id}/0`;
    if (type === 'visual_ads') return asset.image_url ? `${API_URL}${asset.image_url}` : null;
    if (type === 'product_shoots') return asset.shots?.[0]?.image_url ? `${API_URL}${asset.shots[0].image_url}` : null;
    if (type === 'animation_episodes') return asset.thumbnail_url ? `${API_URL}${asset.thumbnail_url}` : null;
    if (type === 'visual_carousels') return asset.slides?.[0]?.image_url ? `${API_URL}${asset.slides[0].image_url}` : null;
    if (type === 'brand_packs') return null; // shown as a card with metadata
    if (type === 'launch_pages') return null;
    return null;
  };

  const getVideoUrl = () => {
    if (type === 'videos' && asset.video_url) return `${API_URL}${asset.video_url}`;
    if (type === 'animation_episodes' && asset.video_url) return `${API_URL}${asset.video_url}`;
    return null;
  };

  const getTitle = () => {
    if (type === 'uploads') return asset.original_name || 'Upload';
    if (type === 'avatars') return `${asset.style || 'Avatar'} Character`;
    if (type === 'carousels') return asset.topic || 'Carousel';
    if (type === 'videos') return asset.product_name || 'Video Ad';
    if (type === 'flyers') return asset.headline || 'Flyer';
    if (type === 'visual_ads') return asset.headline || '3D Visual Ad';
    if (type === 'product_shoots') return `${asset.style_label || 'Shoot'} · ${asset.shots?.length || 0} shots`;
    if (type === 'animation_episodes') return asset.title || 'Episode';
    if (type === 'visual_carousels') return asset.title || 'Visual Carousel';
    if (type === 'brand_packs') return `${asset.brand_name || 'Brand Pack'}`;
    if (type === 'launch_pages') return asset.title || asset.business_name || 'Landing Page';
    return 'Asset';
  };

  const getDate = () => {
    if (!asset.created_at) return '';
    try { return new Date(asset.created_at).toLocaleDateString(); } catch { return ''; }
  };

  const getId = () => {
    return asset.file_id || asset.char_id || asset.carousel_id || asset.video_id || asset.flyer_id || '';
  };

  const imgUrl = getImageUrl();
  const videoUrl = getVideoUrl();
  const isImage = type === 'uploads' ? asset.file_type === 'image' : true;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="group rounded-xl overflow-hidden"
      style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}
      data-testid={`asset-card-${type}-${getId()}`}
    >
      <div className="aspect-square relative overflow-hidden" style={{ background: '#0a0a0a' }}>
        {videoUrl ? (
          <video
            src={videoUrl}
            className="w-full h-full object-cover"
            muted
            onMouseEnter={e => e.target.play()}
            onMouseLeave={e => { e.target.pause(); e.target.currentTime = 0; }}
          />
        ) : imgUrl && isImage ? (
          <img src={imgUrl} alt={getTitle()} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <FolderOpen size={32} className="text-zinc-600" />
          </div>
        )}

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          {imgUrl && (
            <a
              href={imgUrl}
              target="_blank"
              rel="noreferrer"
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
              data-testid={`asset-view-${getId()}`}
            >
              <Eye size={16} className="text-white" />
            </a>
          )}
          {(imgUrl || videoUrl) && (
            <a
              href={imgUrl || videoUrl}
              download
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
              data-testid={`asset-download-${getId()}`}
            >
              <DownloadSimple size={16} className="text-white" />
            </a>
          )}
          <button
            onClick={() => onDelete(type, getId())}
            className="p-2 rounded-lg bg-red-500/20 hover:bg-red-500/40 transition-colors"
            data-testid={`asset-delete-${getId()}`}
          >
            <Trash size={16} className="text-red-400" />
          </button>
        </div>

        {/* Type badge */}
        <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider"
          style={{ background: 'rgba(200,240,96,0.12)', color: '#c8f060' }}
        >
          {type === 'uploads' ? asset.file_type : type.slice(0, -1)}
        </div>
      </div>

      <div className="px-3 py-2.5">
        <p className="text-xs font-medium truncate" style={{ color: '#f5f0e8' }}>{getTitle()}</p>
        <p className="text-[10px] mt-0.5" style={{ color: 'rgba(245,240,232,0.3)' }}>{getDate()}</p>
      </div>
    </motion.div>
  );
};

export const AssetsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'all';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [assets, setAssets] = useState({});
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchAssets = useCallback(async () => {
    if (activeTab === 'blog') { setLoading(false); return; }  // blog has its own fetcher
    setLoading(true);
    try {
      const typeParam = activeTab === 'all' ? '' : `?asset_type=${activeTab}`;
      const [assetsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/assets/all${typeParam}`),
        axios.get(`${API_URL}/api/assets/stats`),
      ]);
      setAssets(assetsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to fetch assets:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);

  const handleDelete = async (type, id) => {
    try {
      const endpoints = {
        uploads: `/api/uploads/${id}`,
        avatars: `/api/characters/${id}`,
        carousels: `/api/carousel/${id}`,
        videos: `/api/video-ads/${id}`,
        flyers: `/api/flyers/${id}`,
        visual_ads: `/api/visual-ad/${id}`,
        product_shoots: `/api/product-shoot/${id}`,
        animation_episodes: `/api/animation/episode/${id}`,
        visual_carousels: `/api/visual-ad/carousel/${id}`,
        brand_packs: null,
        launch_pages: `/api/launch-page/${id}`,
      };
      if (!endpoints[type]) return;
      await axios.delete(`${API_URL}${endpoints[type]}`);
      fetchAssets();
    } catch {}
  };

  const getAllAssets = () => {
    const all = [];
    for (const [type, items] of Object.entries(assets)) {
      if (Array.isArray(items)) {
        items.forEach(item => all.push({ ...item, _assetType: type }));
      }
    }
    // Sort by date desc
    all.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

    if (!search) return all;
    const q = search.toLowerCase();
    return all.filter(a => {
      const name = a.original_name || a.headline || a.topic || a.product_name || a.style || '';
      return name.toLowerCase().includes(q);
    });
  };

  const getTabAssets = () => {
    if (activeTab === 'all') return getAllAssets();
    const items = assets[activeTab] || [];
    if (!search) return items.map(a => ({ ...a, _assetType: activeTab }));
    const q = search.toLowerCase();
    return items
      .filter(a => {
        const name = a.original_name || a.headline || a.topic || a.product_name || a.style || '';
        return name.toLowerCase().includes(q);
      })
      .map(a => ({ ...a, _assetType: activeTab }));
  };

  const displayAssets = getTabAssets();

  const createActions = [
    { label: 'Upload File', icon: Plus, action: () => document.getElementById('asset-upload-input')?.click() },
    { label: 'Create Avatar', icon: Sparkle, action: () => navigate('/dashboard/character-creator') },
    { label: 'Create Carousel', icon: SquaresFour, action: () => navigate('/dashboard/carousel-creator') },
    { label: 'Create Video', icon: VideoCamera, action: () => navigate('/dashboard/ad-creator') },
    { label: 'Create Flyer', icon: Newspaper, action: () => navigate('/dashboard/flyer-creator') },
  ];

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API_URL}/api/uploads/file`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      fetchAssets();
    } catch {}
    e.target.value = '';
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="assets-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold" style={{ color: '#f5f0e8', fontFamily: "'Instrument Serif', serif" }}>
              Assets
            </h1>
            <p className="text-sm mt-1" style={{ color: 'rgba(245,240,232,0.4)' }}>
              {stats.total || 0} total assets across all categories
            </p>
          </div>
          <div className="flex items-center gap-2">
            {createActions.map(({ label, icon: Icon, action }) => (
              <button
                key={label}
                onClick={action}
                data-testid={`asset-action-${label.toLowerCase().replace(/\s+/g, '-')}`}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all hover:scale-[1.02]"
                style={{
                  background: label === 'Upload File' ? '#c8f060' : 'rgba(255,255,255,0.04)',
                  color: label === 'Upload File' ? '#080808' : 'rgba(245,240,232,0.5)',
                  border: label === 'Upload File' ? 'none' : '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <Icon size={14} weight={label === 'Upload File' ? 'bold' : 'regular'} />
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>
          <input id="asset-upload-input" type="file" className="hidden" onChange={handleFileUpload} data-testid="asset-upload-input" />
        </div>

        {/* Tabs + Search */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-1 overflow-x-auto hide-scrollbar">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`assets-tab-${tab.id}`}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all"
                style={{
                  background: activeTab === tab.id ? 'rgba(200,240,96,0.08)' : 'transparent',
                  color: activeTab === tab.id ? '#c8f060' : 'rgba(245,240,232,0.35)',
                  border: activeTab === tab.id ? '1px solid rgba(200,240,96,0.15)' : '1px solid transparent',
                }}
              >
                <tab.icon size={14} weight={activeTab === tab.id ? 'fill' : 'regular'} />
                {tab.label}
                {stats[tab.id] !== undefined && (
                  <span className="ml-1 text-[10px] opacity-50">({stats[tab.id]})</span>
                )}
              </button>
            ))}
          </div>
          <div className="relative sm:ml-auto">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2" size={14} style={{ color: 'rgba(245,240,232,0.25)' }} />
            <input
              type="text"
              placeholder="Search assets..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="assets-search"
              className="pl-8 pr-3 py-1.5 text-xs w-48 focus:ring-1 rounded-lg"
              style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.07)', color: '#f5f0e8', outline: 'none' }}
            />
          </div>
        </div>

        {/* Asset Grid (or Blog list) */}
        {activeTab === 'blog' ? (
          <BlogPostList search={search} />
        ) : loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="aspect-square rounded-xl animate-pulse" style={{ background: '#141414' }} />
            ))}
          </div>
        ) : displayAssets.length === 0 ? (
          <div className="py-16 text-center" data-testid="assets-empty-state">
            <div className="w-20 h-20 rounded-2xl bg-white/[0.03] border border-white/[0.07] flex items-center justify-center mx-auto mb-5">
              <FolderOpen size={36} style={{ color: 'rgba(245,240,232,0.12)' }} />
            </div>
            <h3 className="text-lg font-medium" style={{ color: 'rgba(245,240,232,0.5)', fontFamily: "'Instrument Serif', serif" }}>
              {search ? 'No matching assets' : activeTab === 'all' ? 'Your asset library is empty' : `No ${activeTab} yet`}
            </h3>
            <p className="text-sm mt-2 max-w-sm mx-auto leading-relaxed" style={{ color: 'rgba(245,240,232,0.25)' }}>
              {activeTab === 'avatars' ? 'Create your first brand character to see it here.' :
               activeTab === 'flyers' ? 'Design a flyer in the editor to start building your library.' :
               activeTab === 'carousels' ? 'Build your first carousel post — it will show up here automatically.' :
               activeTab === 'videos' ? 'Create a video ad to see it here.' :
               activeTab === 'uploads' ? 'Upload images or media files to use across your content.' :
               'Create flyers, carousels, videos, or characters to build your library.'}
            </p>
            <div className="flex items-center justify-center gap-3 mt-6">
              {activeTab === 'avatars' && (
                <button onClick={() => navigate('/dashboard/character-creator')} data-testid="create-first-avatar-btn"
                  className="px-5 py-2.5 rounded-xl text-sm font-medium" style={{ background: '#c8f060', color: '#080808' }}>
                  Create Character
                </button>
              )}
              {activeTab === 'flyers' && (
                <button onClick={() => navigate('/dashboard/flyer-creator')} data-testid="create-first-flyer-btn"
                  className="px-5 py-2.5 rounded-xl text-sm font-medium" style={{ background: '#c8f060', color: '#080808' }}>
                  Create Flyer
                </button>
              )}
              {activeTab === 'carousels' && (
                <button onClick={() => navigate('/dashboard/carousel-editor')} data-testid="create-first-carousel-btn"
                  className="px-5 py-2.5 rounded-xl text-sm font-medium" style={{ background: '#c8f060', color: '#080808' }}>
                  Create Carousel
                </button>
              )}
              {activeTab === 'videos' && (
                <button onClick={() => navigate('/dashboard/ad-creator')} data-testid="create-first-video-btn"
                  className="px-5 py-2.5 rounded-xl text-sm font-medium" style={{ background: '#c8f060', color: '#080808' }}>
                  Create Video
                </button>
              )}
              {(activeTab === 'all' || activeTab === 'uploads') && (
                <button onClick={() => document.getElementById('upload-input')?.click()} data-testid="upload-first-asset-btn"
                  className="px-5 py-2.5 rounded-xl text-sm font-medium border border-white/10" style={{ color: 'rgba(245,240,232,0.6)' }}>
                  <Plus size={14} className="inline mr-1.5" /> Upload File
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {displayAssets.map((asset, i) => (
              <AssetCard
                key={asset.file_id || asset.char_id || asset.carousel_id || asset.video_id || asset.flyer_id || i}
                asset={asset}
                type={asset._assetType}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AssetsPage;
